import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DELIVERY_ADDRESS_SEARCH_FORM_MODULE_DATA from '../Data/Defaults';
import { LOCAL_STORAGE_KEYS, ROUTE_URL } from 'common/constants/SharedConstants';
import DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS from 'organisms/DeliveryAddressSearchForm/Constants/DeliveryAddressSearchFormConstants';
import { UserOrderContext } from 'context/context';
import START_ORDER_CONSTANTS from '../../StartOrderComponent/Constants/startOrderConstants';
import { translateWithI18Next } from '@kfc-global/kfc-i18n/lib';
import { hideLoader, showLoader } from '@kfc-global/react-shared/redux/Actions/LoaderAction';
import { FORM, deliveryAddressSearchFormConfig } from '../Config/deliveryAddressSearchForm.config';
import DeliveryAddressSearchForm from './DeliveryAddressSearchForm';
import { DeliveryAddressSearchFormInsideModal } from '../Presentation/DeliveryAddressSearchFormInsideModal';
import { getDispositionTypeOnAPIFormat } from 'common/utilities/utils';
import {
  callStatusAPI,
  selectedAddressDefault,
} from '../DeliveryAddressSearchFormUtilities/DeliveryAddressSearchFormUtils';
import { getDeliveryLocalisationContent } from 'common/utilities/content';
import Strings from '@kfc-global/react-shared/constants/Strings';

/**
 * DeliveryAddressSearchFormModule - Container to render the delivery address search form inside a modal
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export const DeliveryAddressSearchFormModule = props => {
  const { contentData = {}, deliverySearchType, setDeliverySearchType } = props;
  const {
    CHANGE_SCHEDULE_MODE,
    CURRENT_STORE_INFO,
    DISPOSITION_DATA,
    FAV_STORE_NOT_IN_RADIUS,
    FROM_REORDER_MODAL_OPEN,
    REORDER_MODAL_OPEN,
    REORDER_STORE_CHANGED,
    SCHEDULE_ORDER_DATA,
    SEARCH_ORDER_DATA,
    SET_FIND_A_KFC_VIEW,
    SET_ORDER_NOW,
    SET_REDIRECTION_PATH,
    SET_SCHEDULE_ORDER,
    SCHEDULE_ORDER_CLICKED,
    SHOW_SCHEDULE_DISPOSITION,
  } = START_ORDER_CONSTANTS;
  const { CONFIG_SEARCH_SCREEN, CURRENT_STORE_INFO_STORAGE, DISPOSITION_TYPE, RENDER_COMP, SET_CART_CLEAR } =
    LOCAL_STORAGE_KEYS;
  const { POSTAL_CODE_CONTENTFUL, CHOMES_CONTENTFUL, DROPOFF_ADDRESS } = DELIVERY_ADDRESS_SEARCH_FORM_CONSTANTS;

  const dispatch = useDispatch();
  const { userOrderState, userOrderStateDispatch } = useContext(UserOrderContext);
  const { dispositionStateData, searchStoreData, scheduleOrderData } = userOrderState;
  const { changeFlow = false } = scheduleOrderData || {};
  const { dispositionType = localStorage.getItem(DISPOSITION_TYPE) } = dispositionStateData || '';
  const { componentOptions, tenantId } = useSelector(data => data.tenantReducer || {});
  const dispositionConfig = componentOptions?.web?.dispositionConfig;
  const { searchStoreModalContent = {} } = contentData;
  const { formContent = {} } = searchStoreModalContent;
  const [isConfirmingDeliveryAddress, setIsConfirmingDeliveryAddress] = React.useState(false);
  const { configSearchScreen = localStorage.getItem(CONFIG_SEARCH_SCREEN) } = searchStoreData;
  const configSearchKey = `${dispositionType.toUpperCase()}_${configSearchScreen}`;

  const { modalHeaderTitle, shouldShowDisposition, shouldShowSearchStore } = {
    ...deliveryAddressSearchFormConfig?.[configSearchKey],
    ...dispositionConfig?.[configSearchKey],
  };
  const headerTitle = modalHeaderTitle;
  const {
    storesWithStatus = [],
    deliveryLocalization = null,
    isDeliveryLocalizationLoading = true,
    storesSatusError = false,
  } = useSelector(data => data.storesReducer || {});
  const [selectedAddress, setSelectedAddress] = useState(selectedAddressDefault);

  /**
   * selectStoreForDelivery - call to handle selecting the store from storeStatus api call data
   * @param store {Object} - store status data
   */
  const selectStoreForDelivery = useCallback(
    store => {
      userOrderStateDispatch({
        type: SCHEDULE_ORDER_DATA,
        value: {
          showScheduleOrderCatering: true,
          store,
        },
      });
      userOrderStateDispatch({
        type: SEARCH_ORDER_DATA,
        value: { showSearchStore: false },
      });
      userOrderStateDispatch({
        type: SHOW_SCHEDULE_DISPOSITION,
        value: { showSheduleDisposition: true },
      });
      userOrderStateDispatch({
        type: CURRENT_STORE_INFO,
        value: store,
      });
      userOrderStateDispatch({
        type: CHANGE_SCHEDULE_MODE,
        value: { changeScheduleModeFlag: false },
      });
      userOrderStateDispatch({
        type: SCHEDULE_ORDER_CLICKED,
        value: { scheduleModeClicked: true },
      });
      userOrderStateDispatch({
        type: SET_ORDER_NOW,
        value: { showOrderNow: false },
      });
      userOrderStateDispatch({
        type: SET_SCHEDULE_ORDER,
        value: { showScheduleOrder: true },
      });
      userOrderStateDispatch({
        type: FAV_STORE_NOT_IN_RADIUS,
        value: { showNotInRadiusError: false },
      });

      // update currently selected store info
      localStorage.setItem(CURRENT_STORE_INFO_STORAGE, JSON.stringify(store));
    },
    [
      CHANGE_SCHEDULE_MODE,
      CURRENT_STORE_INFO,
      CURRENT_STORE_INFO_STORAGE,
      FAV_STORE_NOT_IN_RADIUS,
      SCHEDULE_ORDER_CLICKED,
      SCHEDULE_ORDER_DATA,
      SEARCH_ORDER_DATA,
      SET_ORDER_NOW,
      SET_SCHEDULE_ORDER,
      SHOW_SCHEDULE_DISPOSITION,
      userOrderStateDispatch,
    ],
  );

  useEffect(() => {
    storesWithStatus.length > 0 && isConfirmingDeliveryAddress && selectStoreForDelivery(storesWithStatus[0]);
  }, [dispositionType, isConfirmingDeliveryAddress, selectStoreForDelivery, storesWithStatus]);

  /**
   * handleClose - Method to handle close button click on modal behavior
   **/
  const handleClose = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();
      localStorage.removeItem(SET_CART_CLEAR);
      localStorage.setItem(RENDER_COMP, true);
      if (!changeFlow) {
        userOrderStateDispatch({ type: SCHEDULE_ORDER_DATA, value: { selectedAddress: {} } });
      }
      userOrderStateDispatch({ type: DISPOSITION_DATA, value: { showDisposition: false } });
      userOrderStateDispatch({ type: SEARCH_ORDER_DATA, value: { showSearchStore: false } });
      userOrderStateDispatch({ type: SET_REDIRECTION_PATH, value: { redirectionOrder: '' } });
      userOrderStateDispatch({ type: REORDER_MODAL_OPEN, value: { reorderModalOpen: false } });
      userOrderStateDispatch({ type: FROM_REORDER_MODAL_OPEN, value: { fromReorderFlag: false } });
      userOrderStateDispatch({ type: REORDER_STORE_CHANGED, value: { storeChanged: false } });
      userOrderStateDispatch({
        type: SET_FIND_A_KFC_VIEW,
        value: { findAKFC: window.location.pathname === ROUTE_URL.FIND_STORE },
      });
    },
    [
      changeFlow,
      DISPOSITION_DATA,
      FROM_REORDER_MODAL_OPEN,
      RENDER_COMP,
      REORDER_MODAL_OPEN,
      REORDER_STORE_CHANGED,
      SCHEDULE_ORDER_DATA,
      SEARCH_ORDER_DATA,
      SET_CART_CLEAR,
      SET_FIND_A_KFC_VIEW,
      SET_REDIRECTION_PATH,
      userOrderStateDispatch,
    ],
  );

  useEffect(() => {
    const closeDeliveryAddressSearchModal = eventOfDeliveryAddressSearchModal => {
      if (eventOfDeliveryAddressSearchModal.keyCode === 27) {
        handleClose(eventOfDeliveryAddressSearchModal);
      }
    };
    window.addEventListener('keydown', closeDeliveryAddressSearchModal);
    return () => {
      window.removeEventListener('keydown', closeDeliveryAddressSearchModal);
    };
  }, [handleClose]);

  /**
   * Method to handle go back button click on modal behavior
   */
  const handleGoBackGlobal = event => {
    event.stopPropagation();
    event.preventDefault();
    userOrderStateDispatch({
      type: DISPOSITION_DATA,
      value: { showDisposition: shouldShowDisposition },
    });
    if (!changeFlow) {
      userOrderStateDispatch({
        type: SCHEDULE_ORDER_DATA,
        value: { selectedAddress: {} },
      });
    }
    userOrderStateDispatch({
      type: SEARCH_ORDER_DATA,
      value: {
        showSearchStore: shouldShowSearchStore,
        configSearchScreen: FORM,
        searchedPlaceNonLocalized: searchStoreData?.searchedPlaceNonLocalized,
      },
    });
  };

  /**
   * @function saveAddressData - Set formatted address data into local storage and User Context
   * @param {String} deliveryAddress - Final user address
   * @param {String} deliveryAddressLine - Optional data. Ex Apt number.
   * @param {[String]} storeInfo - Store Id, Chome Id, City Id
   */
  const saveAddressData = (deliveryAddress, deliveryAddressLine, storeInfo) => {
    const [storeId, chomeId, cityId] = storeInfo;
    localStorage.setItem(DROPOFF_ADDRESS, JSON.stringify(deliveryAddress));
    userOrderStateDispatch({
      type: SCHEDULE_ORDER_DATA,
      value: {
        deliveryAddress,
        deliveryAddressLine,
        selectedAddress: {
          ...selectedAddress,
          chomeId,
          cityId: String(cityId),
        },
        storeId,
      },
    });
  };

  /**
   * @function getStoreData - Get the details of the store selected needed to the schedule delivery
   * @param {[String]} storeInfo - [store id, chome id, city id] of the store filtered by the localisation selection
   */
  const getStoreData = storeInfo => {
    setIsConfirmingDeliveryAddress(true);
    const [storeId] = storeInfo;
    const serviceType = getDispositionTypeOnAPIFormat(dispositionType.toUpperCase());
    const listStores = [{ id: storeId }];
    callStatusAPI(listStores, serviceType, dispatch, tenantId);
  };

  /**
   * @function onDeliveryTypeChanged - Callback called when the user changes the tabs
   * @param {Object} newDeliverySearchType - Object of the delivery selection
   */
  const onDeliveryTypeChanged = newDeliverySearchType => {
    setDeliverySearchType(newDeliverySearchType);
  };

  const orderModeGlobal = (
    <DeliveryAddressSearchForm
      formContent={formContent}
      dispositionType={dispositionType?.toUpperCase()}
      deliveryLocalizationData={deliveryLocalization}
      getStoreData={getStoreData}
      saveAddressData={saveAddressData}
      selectedAddress={selectedAddress}
      setSelectedAddress={setSelectedAddress}
      deliverySearchType={deliverySearchType}
      onDeliveryTypeChanged={onDeliveryTypeChanged}
      isDeliveryLocalizationLoading={isDeliveryLocalizationLoading}
      storesStatusError={storesSatusError}
    />
  );

  /**
   * Get the localisation data
   */
  useEffect(() => {
    dispatch(showLoader());
    const deliveryKey = deliverySearchType?.id === Strings.postalCode ? POSTAL_CODE_CONTENTFUL : CHOMES_CONTENTFUL;
    dispatch(getDeliveryLocalisationContent(deliveryKey));
  }, [dispatch, deliverySearchType?.id, POSTAL_CODE_CONTENTFUL, CHOMES_CONTENTFUL]);

  /**
   * Validate the delivery data state to hide the loading animation
   */
  useEffect(() => {
    //If the data is no longer loading, hide the loader
    if (!isDeliveryLocalizationLoading && deliveryLocalization) {
      dispatch(hideLoader());
    }
  }, [isDeliveryLocalizationLoading, deliveryLocalization, dispatch]);

  return (
    <DeliveryAddressSearchFormInsideModal
      handleClose={handleClose}
      orderModeTypeItem={orderModeGlobal}
      headerText={translateWithI18Next(headerTitle)}
      handleGoBack={handleGoBackGlobal}
      modalTitleId='delivery-address-search-form'
      className='search-store-global-container'
      backDataTestId='back-btn-global-modal-id'
    />
  );
};

// Defaults from defaults json file
DeliveryAddressSearchFormModule.defaultProps = DELIVERY_ADDRESS_SEARCH_FORM_MODULE_DATA;

export default DeliveryAddressSearchFormModule;
